import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Send,
  Loader2,
  Bot,
  User,
  X,
  Minus,
  Maximize2,
  Plus,
} from "lucide-react";
import { cn } from "../lib/utils";
import getBITSEndApiMap from "../apiMap";
import axios from "axios";

interface Message {
  id: string;
  sender: "user" | "ai";
  text: string;
  timestamp: Date;
  frame?: string;
  question?: string;
  suggestedActions?: string[];
}

interface AITutorChatProps {
  projectId: string;
  isSidebar?: boolean;
  onClose?: () => void;
}

interface SessionInfo {
  session_id: string;
  created_at: string | null;
}

const DEFAULT_TEMPLATE_SLUG = "socratic";

const AITutorChat: React.FC<AITutorChatProps> = ({
  projectId,
  isSidebar = false,
  onClose,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState<
    string | null
  >(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen || isSidebar) {
      scrollToBottom();
    }
  }, [messages, isOpen, isMinimized, isSidebar]);

  // Create a new conversation with Fishbone API
  const createNewConversation = useCallback(async (initialMessage: string) => {
    try {
      setIsLoading(true);

      const createUrl = getBITSEndApiMap("fishboneCreateConversation");
      const createResponse = await axios.post(createUrl, {
        templateSlug: DEFAULT_TEMPLATE_SLUG,
        initialMessage,
      });

      const conversationId = createResponse.data?.conversationId;

      if (!conversationId) {
        throw new Error("No conversation ID returned");
      }

      setCurrentConversationId(conversationId);

      // Send the first message to get the AI response
      const chatUrl = getBITSEndApiMap("fishboneSendMessage");
      const chatResponse = await axios.post(chatUrl, {
        templateSlug: DEFAULT_TEMPLATE_SLUG,
        message: initialMessage,
        context: {
          previousMessages: [],
          reasoningLevel: 1,
        },
      });

      const aiResponse = chatResponse.data;

      const userMsg: Message = {
        id: Date.now().toString(),
        sender: "user",
        text: initialMessage,
        timestamp: new Date(),
      };

      const aiMsg: Message = {
        id: (Date.now() + 1).toString(),
        sender: "ai",
        text: aiResponse.reply || "",
        timestamp: new Date(),
        frame: aiResponse.frame || "",
        question: aiResponse.question || "",
        suggestedActions: aiResponse.suggestedActions || [],
      };

      setMessages([userMsg, aiMsg]);
    } catch (error) {
      console.error("Error creating conversation:", error);
      setMessages([
        {
          id: Date.now().toString(),
          sender: "user",
          text: initialMessage,
          timestamp: new Date(),
        },
        {
          id: (Date.now() + 1).toString(),
          sender: "ai",
          text: "I apologize, but I'm having trouble connecting to the AI service. Please try again.",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Send a message to the current conversation
  const sendMessageToConversation = useCallback(
    async (message: string) => {
      if (!currentConversationId) {
        await createNewConversation(message);
        return;
      }

      try {
        setIsLoading(true);

        const chatUrl = getBITSEndApiMap("fishboneSendMessage");
        const response = await axios.post(chatUrl, {
          templateSlug: DEFAULT_TEMPLATE_SLUG,
          message,
          context: {
            previousMessages: messages
              .filter((m) => m.sender === "user" || m.sender === "ai")
              .map((m) => ({
                role: m.sender === "user" ? "user" : "assistant",
                content: m.text,
              })),
            reasoningLevel: 1,
          },
        });

        const aiResponse = response.data;

        const userMsg: Message = {
          id: Date.now().toString(),
          sender: "user",
          text: message,
          timestamp: new Date(),
        };

        const aiMsg: Message = {
          id: (Date.now() + 1).toString(),
          sender: "ai",
          text: aiResponse.reply || "",
          timestamp: new Date(),
          frame: aiResponse.frame || "",
          question: aiResponse.question || "",
          suggestedActions: aiResponse.suggestedActions || [],
        };

        setMessages((prev) => [...prev, userMsg, aiMsg]);
      } catch (error) {
        console.error("Error sending message:", error);
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now().toString(),
            sender: "user",
            text: message,
            timestamp: new Date(),
          },
          {
            id: (Date.now() + 1).toString(),
            sender: "ai",
            text: "I apologize, but I'm having trouble connecting to the AI service. Please try again.",
            timestamp: new Date(),
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [currentConversationId, messages, createNewConversation],
  );

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    const messageToSend = input;
    setInput("");
    await sendMessageToConversation(messageToSend);
  };

  const handleActionClick = async (action: string) => {
    if (isLoading) return;
    await sendMessageToConversation(action);
  };

  const startNewChat = () => {
    setCurrentConversationId(null);
    setMessages([]);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClose = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (onClose) {
      onClose();
    } else {
      setIsOpen(false);
    }
  };

  const cleanMessageText = (text: string) => {
    if (text.startsWith("PROJECT DETAILS:")) {
      const parts = text.split("STUDENT MESSAGE:");
      return parts.length > 1 ? parts[1].trim() : text;
    }
    return text;
  };

  if (!isOpen && !isSidebar) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 p-4 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all z-50 flex items-center justify-center group"
        title="AI Assistant"
      >
        <Bot size={24} className="group-hover:scale-110 transition-transform" />
        <span className="ml-2 font-medium hidden group-hover:block whitespace-nowrap">
          AI Assistant
        </span>
      </button>
    );
  }

  return (
    <div
      className={cn(
        "bg-white flex flex-col transition-all overflow-hidden",
        isSidebar
          ? "h-full w-full border-l border-gray-200"
          : cn(
              "fixed bottom-6 right-6 w-80 border border-gray-200 rounded-xl shadow-2xl z-50",
              isMinimized ? "h-14" : "h-[450px]",
            ),
      )}
    >
      {/* Header */}
      <div
        className={cn(
          "p-3 border-b flex items-center justify-between",
          isSidebar
            ? "bg-white text-gray-900"
            : "bg-blue-600 text-white cursor-pointer",
        )}
        onClick={() => !isSidebar && setIsMinimized(!isMinimized)}
      >
        <div className="flex items-center gap-2">
          <Bot
            size={20}
            className={isSidebar ? "text-blue-600" : "text-white"}
          />

          <div className="bg-white px-6 py-2">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-[14px]  text-gray-900">AI Tutor</h1>
                <p className="text-[10px] text-gray-500">
                  Your AI tutor for learning and projects
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {!isMinimized && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                startNewChat();
              }}
              className="p-1.5 hover:bg-white/20 rounded transition-colors"
              title="New Chat"
            >
              <Plus size={16} />
            </button>
          )}
          {!isSidebar && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMinimized(!isMinimized);
              }}
              className="p-1.5 hover:bg-white/20 rounded transition-colors"
            >
              {isMinimized ? <Maximize2 size={16} /> : <Minus size={16} />}
            </button>
          )}
          <button
            onClick={handleClose}
            className={cn(
              "p-1.5 rounded transition-colors",
              isSidebar
                ? "hover:bg-gray-100 text-gray-500"
                : "hover:bg-white/20 text-white",
            )}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Messages */}
      {(!isMinimized || isSidebar) && (
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 text-center space-y-2">
                <Bot size={40} className="mb-2 opacity-20" />
                <p className="text-sm font-medium">Hello! I'm you AI Tutor.</p>
                <p className="text-xs">What would you like to learn today?</p>
              </div>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={cn(
                  "flex items-start gap-2 max-w-[90%]",
                  msg.sender === "user"
                    ? "ml-auto flex-row-reverse"
                    : "mr-auto",
                )}
              >
                <div
                  className={cn(
                    "p-2 rounded-full flex-shrink-0",
                    msg.sender === "user"
                      ? "bg-blue-100 text-blue-600"
                      : "bg-white border text-gray-600",
                  )}
                >
                  {msg.sender === "user" ? (
                    <User size={14} />
                  ) : (
                    <Bot size={14} />
                  )}
                </div>
                <div
                  className={cn(
                    "p-3 rounded-2xl text-sm shadow-sm",
                    msg.sender === "user"
                      ? "bg-blue-600 text-white rounded-tr-none"
                      : "bg-white text-gray-800 border rounded-tl-none",
                  )}
                >
                  <p className="whitespace-pre-wrap">
                    {cleanMessageText(msg.text)}
                  </p>

                  {msg.frame && msg.sender === "ai" && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <p className="text-xs text-gray-600 whitespace-pre-wrap">
                        {msg.frame}
                      </p>
                    </div>
                  )}

                  {msg.question && msg.sender === "ai" && (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-blue-600">
                        {msg.question}
                      </p>
                    </div>
                  )}

                  {msg.suggestedActions &&
                    msg.suggestedActions.length > 0 &&
                    msg.sender === "ai" && (
                      <div className="mt-3 pt-2 border-t border-gray-100">
                        <p className="text-xs text-gray-500 mb-2">
                          What would you like to do?
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {msg.suggestedActions.map((action, index) => (
                            <button
                              key={index}
                              onClick={() => handleActionClick(action)}
                              disabled={isLoading}
                              className="px-3 py-1.5 text-xs bg-blue-50 text-blue-600 rounded-full hover:bg-blue-100 transition-colors border border-blue-200 disabled:opacity-50"
                            >
                              {action}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex items-center gap-2">
                <div className="bg-white border p-2 rounded-full text-gray-400">
                  <Bot size={14} />
                </div>
                <div className="bg-white border p-3 rounded-2xl rounded-tl-none shadow-sm">
                  <Loader2 size={16} className="animate-spin text-gray-400" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t bg-white">
            <div className="relative flex items-center">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Ask your tutor..."
                className="w-full pl-3 pr-10 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                rows={1}
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                className="absolute right-2 p-1.5 text-blue-600 hover:bg-blue-50 disabled:text-gray-300 rounded-lg transition-colors"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AITutorChat;
